import React, { useCallback } from 'react';
import type { Theme } from '@material-ui/core';
import { Drawer, List } from '@material-ui/core';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { useCurrentUser } from '@klp/auth';
import { useDispatch, useSelector } from 'react-redux';
import {
    contextSelectorActions,
    contextSelectors,
} from '../store/contextSelectorSlice';
import {
    useDeleteFavorittMutation,
    useGetByggListQuery,
} from '../services/rtkApi';
import { useTranslation } from 'react-i18next';
import type { Bygg } from '@forvalter/types/ForvalterCommonDAL';
import { ByggSelectorHeader } from './ByggSelector/ByggSelectorHeader';
import { ByggSelectorToggle } from './ByggSelector/ByggSelectorToggle';
import { VirtualByggItem } from './ByggSelector/VirtualByggItem';
import type { ByggSelectorVirtualListData } from './ByggSelector/types';
import classNames from 'classnames';
import { useLayoutContext } from '@klp/layout/Layout';
import { useReadElementHeight } from '@klp/layout/hooks/useReadElementHeight';
import { FixedSizeList as VirtualList } from 'react-window';
import { theme as klpTheme } from '@klp/design/Theme';
import { showToast } from '@klpkonsern/ui';

const styles = (theme: Theme) => ({
    drawer: {
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[1],
        zIndex: theme.zIndex.drawer - 3,
        marginTop: theme.header.height + 1,
        '& > div': {
            width: `calc(var(--bygg-selector-closed-width))`,
            height: 'auto',
            transition: theme.layout.transition,
            position: 'sticky',
            overflowX: 'hidden',
            border: 0,
            '& > nav.ByggSelector__List': {
                overflowX: 'hidden',
                overflowY: 'auto',
                transition: theme.layout.transition,
                width: `calc(var(--bygg-selector-open-width))`,
                left: `calc((var(--bygg-selector-closed-width) - var(--bygg-selector-open-width)) + 1px)`,
                '& > div': {
                    overflowX: 'hidden !important',
                },
            },
        },
        '& .ByggSelector__ListItem': {
            transition: theme.layout.transition,
            width: `calc(var(--bygg-selector-open-width))`,
            paddingTop: theme.spacing(0.5),
            paddingBottom: theme.spacing(0.5),
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            '& .MuiListItemIcon-root': {
                minWidth: `calc(var(--bygg-selector-closed-width))`,
                width: theme.spacing(7),
                height: theme.spacing(7),
            },
            '& .MuiIconButton-root': {
                padding: theme.spacing(2),
            },
        },
        // Bygg selector open state
        '.ByggSelectorOpen &': {
            '& > div': {
                width: `calc(var(--bygg-selector-open-width))`,
                '& > .ByggSelector__List': {
                    left: 0,
                },
            },
            '& .ByggSelector__ListItem': {
                width: `calc(var(--bygg-selector-open-width) - ${theme.spacing(1)}px)`,
            },
        },
    },
});

const ROW_HEIGHT = klpTheme.spacing(9);
const LIST_HEIGHT = window.innerHeight;

export const ByggSelector = React.memo(() => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const { currentUser } = useCurrentUser();
    const { withoutSidebar, isMobile } = useLayoutContext();
    const byggSelectorHeaderRef = React.useRef<HTMLDivElement>(null);
    useReadElementHeight(
        byggSelectorHeaderRef,
        '--bygg-selector-header-height',
    );

    const dispatch = useDispatch();

    const { showByggGrunn, openMineBygg } = useSelector(contextSelectors);

    const {
        saveSelectedContextToStorage,
        reducerActions: { setOpenMineBygg },
    } = contextSelectorActions;

    const { data: byggFavoritterData = [] } = useGetByggListQuery(
        {
            ansvarlig: currentUser?.epost,
            includeAll: false,
        },
        {
            skip: !currentUser?.epost,
            selectFromResult: ({ data, ...rest }) => ({
                data: !showByggGrunn
                    ? data?.filter((bygg) => bygg.isGrunn !== true)
                    : data,
                ...rest,
            }),
        },
    );

    const [
        deleteFavoritt,
        { isError: deleteFavorittError, isLoading: deleteFavorittLoading },
    ] = useDeleteFavorittMutation();

    const handleSelect = useCallback(
        (bygg: Bygg) => {
            dispatch(saveSelectedContextToStorage(bygg));
        },
        [dispatch, saveSelectedContextToStorage],
    );

    const handleDeleteFavoritt = useCallback(
        (favorittId?: number) => {
            if (!favorittId) return;
            deleteFavoritt({
                favorittId,
            });

            if (deleteFavorittError) {
                showToast({
                    body: t('contractRegistration.error'),
                    type: 'error',
                });
            }
        },
        [deleteFavoritt, deleteFavorittError],
    );

    const toggleDrawerOpen = useCallback(() => {
        dispatch(setOpenMineBygg(!openMineBygg));
    }, [dispatch, setOpenMineBygg, openMineBygg]);

    // Memoize the data object to prevent VirtualList re-renders
    const virtualListData: ByggSelectorVirtualListData = React.useMemo(
        () => ({
            byggFavoritterData,
            openMineBygg,
            disabled: deleteFavorittLoading,
            onSelect: handleSelect,
            onDelete: handleDeleteFavoritt,
        }),
        [
            byggFavoritterData,
            openMineBygg,
            deleteFavorittLoading,
            handleSelect,
            handleDeleteFavoritt,
        ],
    );

    if (withoutSidebar) return null;
    if (isMobile) return null;

    return (
        <Drawer
            variant="permanent"
            anchor="left"
            className={classNames('ByggSelector', classes.drawer)}
        >
            <List
                component="nav"
                disablePadding={true}
                ref={byggSelectorHeaderRef}
                data-testid="ByggSelector__Header"
            >
                <ByggSelectorToggle
                    open={openMineBygg}
                    toggleOpen={toggleDrawerOpen}
                />
                <ByggSelectorHeader />
            </List>
            <List
                component="nav"
                disablePadding={true}
                className="ByggSelector__List"
            >
                <VirtualList
                    height={LIST_HEIGHT}
                    width="auto"
                    itemCount={byggFavoritterData.length}
                    itemSize={ROW_HEIGHT}
                    itemData={virtualListData}
                >
                    {VirtualByggItem}
                </VirtualList>
            </List>
        </Drawer>
    );
});

ByggSelector.displayName = 'ByggSelector';
